import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Select, InputNumber, Button, Space, Row, Col, Typography,
  Tag, message, Alert, Divider, Input, Modal, Statistic
} from 'antd';
import {
  DeleteOutlined, PlusOutlined, EyeOutlined, ClearOutlined, WarningOutlined
} from '@ant-design/icons';
import { securityAPI, authAPI, itemAPI } from '../services/api';

const { Text, Title, Paragraph } = Typography;
const { Option, OptGroup } = Select;
const { TextArea } = Input;

// 置顶三币(名称 + 编号,幸运币两个必须靠编号区分)
const PINNED_ITEMS = [
  { gid: 67174445, name: 'AOZ', scope: 'global' },
  { gid: 67174446, name: '免费AOZ', scope: 'global' },
  { gid: 67174447, name: 'GM AOZ', scope: 'global' },
  { gid: 67174411, name: '黄金', scope: 'global' },
  { gid: 67174413, name: '免费黄金', scope: 'global' },
  { gid: 67174426, name: '幸运币(全局)', scope: 'global' },
  { gid: 67240300, name: '幸运币(背包)', scope: 'item' }
];

// 全局资源 gid(用于显示"全局"标签;后端已支持清空,不再拦截)
const GLOBAL_GIDS = new Set([67174445, 67174446, 67174447, 67174411, 67174413, 67174426]);

// 平台币哨兵:道具下拉里选"平台币"时使用该值(与区服无关,清账号钱包)。
const PLATFORM_COIN_GID = 'platform_coin';

// 定位方式(账号维度 → 该区角色;角色维度 → 直接定位角色)。
const MATCH_TYPE_OPTIONS = [
  { value: 'account', label: '账号' },
  { value: 'accountId', label: '账号ID' },
  { value: 'roleName', label: '角色名称' },
  { value: 'roleId', label: '角色ID' }
];
const MATCH_TYPE_HINT = {
  account: '按账号名定位:清该账号在所选区服下角色名下的道具/资源',
  accountId: '按账号ID(sys_user.user_id)定位:清该账号在所选区服下角色名下的道具/资源',
  roleName: '按角色名称定位:清所选区服下同名角色的道具/资源',
  roleId: '按角色ID(游戏内角色id)定位:清所选区服下该角色的道具/资源'
};
const MATCH_TYPE_PLACEHOLDER = {
  account: '例如:\nplayer_abc\nplayer_def',
  accountId: '例如:\n100001\n100002',
  roleName: '例如:\n幸存者小明\n老王',
  roleId: '例如:\n5200001\n5200002'
};

const MAX_ACCOUNTS = 200;
const MAX_ITEMS = 10;

let rowSeq = 0;
function newRow() {
  rowSeq += 1;
  return { key: `row-${rowSeq}`, gid: undefined, level: undefined };
}

function parseAccounts(text) {
  return String(text || '')
    .split(/[\s,;，；\n\r\t]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function ItemClearManagement() {
  const [zones, setZones] = useState([]);
  const [serverId, setServerId] = useState(undefined);
  const [matchType, setMatchType] = useState('account');
  const [accountsText, setAccountsText] = useState('');
  const [items, setItems] = useState([newRow()]);
  const [itemOptions, setItemOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [result, setResult] = useState(null);
  const [resultMode, setResultMode] = useState(null); // 'preview' | 'clear'

  // 加载区服列表
  useEffect(() => {
    (async () => {
      try {
        const resp = await authAPI.getZones();
        if (resp.data && resp.data.success) {
          const list = resp.data.data.zones || [];
          const seen = new Map();
          list.forEach((z) => {
            const sid = Number(z.serverId);
            if (Number.isFinite(sid) && sid > 0 && !seen.has(sid)) {
              seen.set(sid, { serverId: sid, name: z.label || z.name || `区服${sid}` });
            }
          });
          const zoneList = [...seen.values()].sort((a, b) => a.serverId - b.serverId);
          setZones(zoneList);
          if (zoneList.length > 0) {
            setServerId(zoneList[0].serverId);
          }
        }
      } catch (e) {
        console.error('加载区服失败', e);
        message.error('加载区服列表失败');
      }
    })();
  }, []);

  const searchItems = useCallback(async (keyword) => {
    if (!keyword || String(keyword).trim().length === 0) {
      setItemOptions([]);
      return;
    }
    setSearching(true);
    try {
      const resp = await itemAPI.getList({ keyword: String(keyword).trim(), page: 1, limit: 50 });
      if (resp.data && resp.data.success) {
        const list = (resp.data.data.items || []).map((it) => ({
          gid: Number(it.item_id),
          name: it.name || String(it.item_id)
        })).filter((it) => Number.isFinite(it.gid));
        setItemOptions(list);
      }
    } catch (e) {
      console.error('搜索道具失败', e);
    } finally {
      setSearching(false);
    }
  }, []);

  const updateRow = (key, patch) => {
    setItems((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };
  const addRow = () => setItems((rows) => (rows.length >= MAX_ITEMS ? rows : [...rows, newRow()]));
  const removeRow = (key) => {
    setItems((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.key !== key)));
  };

  // 道具下拉选择:选"平台币"时收敛为单行(与区服/背包道具互斥),并把定位方式限制为 账号/账号ID。
  const onSelectItemGid = (key, val) => {
    if (String(val) === PLATFORM_COIN_GID) {
      setItems([{ key, gid: PLATFORM_COIN_GID, level: undefined }]);
      setMatchType((mt) => (mt === 'account' || mt === 'accountId' ? mt : 'account'));
    } else {
      updateRow(key, { gid: val });
    }
  };

  const accountList = useMemo(() => parseAccounts(accountsText), [accountsText]);

  const hasGlobalSelected = useMemo(
    () => items.some((r) => r.gid && GLOBAL_GIDS.has(Number(r.gid))),
    [items]
  );

  // 是否选择了平台币(账号维度、与区服无关)。
  const hasPlatformCoin = useMemo(
    () => items.some((r) => String(r.gid) === PLATFORM_COIN_GID),
    [items]
  );

  // 校验并构造 payload;非法返回 null 并提示
  const buildPayload = () => {
    if (accountList.length === 0) {
      message.warning('请填写至少 1 个定位值');
      return null;
    }
    if (accountList.length > MAX_ACCOUNTS) {
      message.warning(`定位值数量最多 ${MAX_ACCOUNTS} 个,当前 ${accountList.length} 个`);
      return null;
    }

    // 平台币:与区服无关,只按 账号/账号ID 清账号钱包。
    if (hasPlatformCoin) {
      const otherItems = items.filter(
        (r) => r.gid !== undefined && r.gid !== null && r.gid !== ''
          && String(r.gid) !== PLATFORM_COIN_GID
      );
      if (otherItems.length > 0) {
        message.error('平台币不能与道具/资源一起清空,请分开操作');
        return null;
      }
      if (matchType !== 'account' && matchType !== 'accountId') {
        message.error('清空平台币只支持按 账号 / 账号ID 定位');
        return null;
      }
      return { matchType, accounts: accountList, items: [{ gid: PLATFORM_COIN_GID }] };
    }

    if (!serverId) {
      message.warning('请选择区服');
      return null;
    }

    const cleaned = [];
    const seen = new Set();
    for (let i = 0; i < items.length; i += 1) {
      const r = items[i];
      if (r.gid === undefined || r.gid === null || r.gid === '') continue;
      const gid = Number(r.gid);
      if (!Number.isFinite(gid) || gid <= 0) {
        message.warning(`第 ${i + 1} 个道具 gid 非法`);
        return null;
      }
      const row = { gid };
      if (r.level !== undefined && r.level !== null && r.level !== '') {
        row.level = Number(r.level);
      }
      const dedupKey = `${gid}:${row.level === undefined ? '*' : row.level}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      cleaned.push(row);
    }
    if (cleaned.length === 0) {
      message.warning('请至少选择 1 个道具/资源');
      return null;
    }
    if (cleaned.length > MAX_ITEMS) {
      message.warning(`道具种类最多 ${MAX_ITEMS} 个`);
      return null;
    }

    return { serverId, matchType, accounts: accountList, items: cleaned };
  };

  const runPreview = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setPreviewing(true);
    setResult(null);
    try {
      const resp = await securityAPI.clearItemsPreview(payload);
      if (resp.data && resp.data.success) {
        setResult(resp.data.data);
        setResultMode('preview');
        message.success(`预览完成,解析到 ${resp.data.data.summary.targets_resolved} 个角色`);
      } else {
        message.error((resp.data && resp.data.message) || '预览失败');
      }
    } catch (e) {
      const msg = e.response && e.response.data && e.response.data.message;
      message.error(msg || '预览失败');
    } finally {
      setPreviewing(false);
    }
  };

  const doClear = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setClearing(true);
    setResult(null);
    try {
      const resp = await securityAPI.clearItems(payload);
      if (resp.data && resp.data.success) {
        setResult(resp.data.data);
        setResultMode('clear');
        const s = resp.data.data.summary;
        message.success(`清空完成:成功 ${s.users_ok} 个,失败 ${s.users_failed} 个,跳过 ${s.users_skipped} 个`);
      } else {
        message.error((resp.data && resp.data.message) || '清空失败');
      }
    } catch (e) {
      const msg = e.response && e.response.data && e.response.data.message;
      message.error(msg || '清空失败');
    } finally {
      setClearing(false);
    }
  };

  const confirmClear = () => {
    const payload = buildPayload();
    if (!payload) return;
    const matchLabel = (MATCH_TYPE_OPTIONS.find((o) => o.value === payload.matchType) || {}).label || '定位值';

    if (hasPlatformCoin) {
      Modal.confirm({
        title: '二次确认:清空账号平台币',
        icon: <WarningOutlined style={{ color: '#faad14' }} />,
        width: 520,
        content: (
          <div>
            <Paragraph style={{ marginBottom: 8 }}>
              即将按「{matchLabel}」定位 <Text strong type="danger">{payload.accounts.length}</Text> 个账号,
              将其<Text strong>平台币余额置 0</Text>(与区服无关),该操作<Text strong type="danger">不可撤销</Text>。
            </Paragraph>
            <Text type="secondary">原子事务:锁行读余额 → 写 admin_clear 流水 → 余额清 0、total_spent 累加。</Text>
          </div>
        ),
        okText: '确认清空',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: doClear
      });
      return;
    }

    Modal.confirm({
      title: '二次确认:清空玩家道具/资源',
      icon: <WarningOutlined style={{ color: '#faad14' }} />,
      width: 520,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            即将对区服 <Text strong>{serverId}</Text> 下、按「{matchLabel}」定位的 <Text strong type="danger">{payload.accounts.length}</Text> 个目标
            清空以下 <Text strong type="danger">{payload.items.length}</Text> 种道具/资源,
            该操作<Text strong type="danger">不可撤销</Text>:
          </Paragraph>
          <ul style={{ marginBottom: 8 }}>
            {payload.items.map((it) => (
              <li key={`${it.gid}:${it.level ?? '*'}`}>
                #{it.gid}{it.level !== undefined ? ` (Lv${it.level})` : ' (全部等级)'}
                {GLOBAL_GIDS.has(Number(it.gid)) ? ' [全局资源]' : ''}
              </li>
            ))}
          </ul>
          <Text type="secondary">背包道具走 gmDeleteItem 清空;全局资源(AOZ/黄金等)走 gmRewardItems 负数扣减。在线安全、幂等。</Text>
        </div>
      ),
      okText: '确认清空',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: doClear
    });
  };

  // 结果表:每行一个角色(平台币则每行一个账号),展开每个 (gid,level) 明细
  const resultColumns = useMemo(() => {
    const isPlatform = result && result.mode === 'platform_coin';
    const cols = [
      { title: '输入', dataIndex: 'input', key: 'input', width: 140, ellipsis: true },
      { title: isPlatform ? '账号ID' : '游戏ID', dataIndex: 'game_user_id', key: 'game_user_id', width: 140 },
      { title: isPlatform ? '账号名' : '角色名', dataIndex: 'name', key: 'name', width: 140, ellipsis: true }
    ];
    if (resultMode === 'clear') {
      cols.push({
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: (v) => {
          if (v === 'success') return <Tag color="green">成功</Tag>;
          if (v === 'skipped') return <Tag color="default">跳过</Tag>;
          return <Tag color="red">失败</Tag>;
        }
      });
    }
    cols.push({
      title: '道具明细',
      key: 'items',
      render: (_, record) => {
        const list = record.items || [];
        if (list.length === 0) {
          return <Text type="secondary">{record.error || '无'}</Text>;
        }
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {list.map((it) => (
              <span key={`${it.gid}:${it.level}`}>
                <Text code>{it.label || `#${it.gid} Lv${it.level}`}</Text>{' '}
                {it.kind === 'global' && <Tag color="orange" style={{ marginLeft: 4 }}>全局</Tag>}
                {resultMode === 'clear' ? (
                  <>
                    <Text>前 {Number(it.before_count).toLocaleString()}</Text>
                    {' → '}
                    <Text type={it.result === 'success' ? 'success' : 'danger'}>
                      后 {Number(it.after_count).toLocaleString()}
                    </Text>
                    {it.result !== 'success' && it.error
                      ? <Text type="danger"> ({it.error})</Text> : null}
                  </>
                ) : (
                  <Text>当前 {Number(it.before_count).toLocaleString()}</Text>
                )}
              </span>
            ))}
          </Space>
        );
      }
    });
    return cols;
  }, [resultMode, result]);

  return (
    <div style={{ padding: 16 }}>
      <Title level={4}>
        <ClearOutlined /> 道具清空
      </Title>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="背包道具走游戏服 GM 通道(/api2/gmDeleteItem)清空;全局资源(AOZ/黄金/幸运币全局)走 gmRewardItems 负数扣减;平台币走账号钱包原子事务清 0。均不直接改数据库。"
        description={`定位方式:账号 / 账号ID → 该区角色;角色名称 / 角色ID → 直接定位角色。幸运币有两个编号:67174426(全局)与 67240300(背包)。平台币与区服无关、只按 账号/账号ID 定位、不能与道具混选。批量 ≤ ${MAX_ACCOUNTS},道具种类 ≤ ${MAX_ITEMS}。`}
      />

      <Card title="清空条件" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>区服</Text>{' '}
              {hasPlatformCoin && <Text type="secondary">(平台币与区服无关,已忽略)</Text>}
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="选择区服"
              value={serverId}
              onChange={setServerId}
              disabled={hasPlatformCoin}
              options={zones.map((z) => ({ value: z.serverId, label: `${z.name}(区${z.serverId})` }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}><Text strong>定位方式</Text></div>
            <Select
              style={{ width: '100%' }}
              value={matchType}
              onChange={setMatchType}
              options={MATCH_TYPE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
                disabled: hasPlatformCoin && o.value !== 'account' && o.value !== 'accountId'
              }))}
            />
          </Col>
          <Col xs={24}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>
                {(MATCH_TYPE_OPTIONS.find((o) => o.value === matchType) || {}).label || '定位值'}
              </Text>{' '}
              <Text type="secondary">
                (换行/空格/逗号分隔,可批量;当前 {accountList.length} 个)
              </Text>
              <div><Text type="secondary">{MATCH_TYPE_HINT[matchType]}</Text></div>
            </div>
            <TextArea
              rows={5}
              placeholder={MATCH_TYPE_PLACEHOLDER[matchType] || ''}
              value={accountsText}
              onChange={(e) => setAccountsText(e.target.value)}
            />
          </Col>
        </Row>

        <Divider orientation="left" style={{ marginTop: 20 }}>
          道具 / 资产(背包道具不填等级=清空该道具全部等级;全局资源无需等级)
        </Divider>

        {items.map((row, index) => {
          const isPlatformRow = String(row.gid) === PLATFORM_COIN_GID;
          return (
          <Row gutter={[12, 12]} key={row.key} align="middle" style={{ marginBottom: 10 }}>
            <Col xs={24} sm={12} md={11}>
              <Select
                style={{ width: '100%' }}
                showSearch
                allowClear
                placeholder="选择道具 / 搜索名称"
                value={row.gid}
                filterOption={false}
                loading={searching}
                onSearch={searchItems}
                onChange={(val) => onSelectItemGid(row.key, val)}
                notFoundContent={searching ? '搜索中...' : '输入关键词搜索'}
              >
                <OptGroup label="账号资产(与区服无关)">
                  <Option key="asset-platform-coin" value={PLATFORM_COIN_GID}>
                    平台币
                    <Tag color="purple" style={{ marginLeft: 6 }}>账号·钱包</Tag>
                  </Option>
                </OptGroup>
                <OptGroup label="置顶(三币)">
                  {PINNED_ITEMS.map((it) => (
                    <Option key={`pin-${it.gid}`} value={it.gid}>
                      {it.name}
                      <Text type="secondary"> #{it.gid}</Text>
                      {it.scope === 'global'
                        ? <Tag color="orange" style={{ marginLeft: 6 }}>全局</Tag>
                        : <Tag color="blue" style={{ marginLeft: 6 }}>背包</Tag>}
                    </Option>
                  ))}
                </OptGroup>
                {itemOptions.length > 0 && (
                  <OptGroup label="搜索结果">
                    {itemOptions.map((it) => (
                      <Option key={`item-${it.gid}`} value={it.gid}>
                        {it.name}<Text type="secondary"> #{it.gid}</Text>
                      </Option>
                    ))}
                  </OptGroup>
                )}
              </Select>
            </Col>
            <Col xs={12} sm={6} md={5}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="gid(可手填)"
                min={1}
                disabled={isPlatformRow}
                value={isPlatformRow ? undefined : row.gid}
                onChange={(val) => updateRow(row.key, { gid: val })}
              />
            </Col>
            <Col xs={8} sm={4} md={5}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="等级(可选)"
                min={0}
                disabled={isPlatformRow}
                value={row.level}
                onChange={(val) => updateRow(row.key, { level: val })}
              />
            </Col>
            <Col xs={4} sm={2} md={3}>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={items.length <= 1}
                onClick={() => removeRow(row.key)}
              />
            </Col>
          </Row>
          );
        })}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addRow}
          disabled={items.length >= MAX_ITEMS || hasPlatformCoin}
          style={{ marginTop: 4 }}
        >
          添加道具行
        </Button>

        {hasPlatformCoin && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="已选择「平台币」:清的是账号挂钩的平台币(与区服无关,置 0 并写 admin_clear 流水),仅支持按 账号 / 账号ID 定位,不能与道具混选。"
          />
        )}

        {hasGlobalSelected && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="已选择全局资源(AOZ / 黄金 / 幸运币全局),将通过 gmRewardItems 负数扣减清零。"
          />
        )}

        <Divider />
        <Space>
          <Button icon={<EyeOutlined />} loading={previewing} onClick={runPreview}>
            预览当前数量(只读)
          </Button>
          <Button
            type="primary"
            danger
            icon={<ClearOutlined />}
            loading={clearing}
            onClick={confirmClear}
          >
            清空道具
          </Button>
        </Space>
      </Card>

      {result && (
        <Card
          title={resultMode === 'clear' ? '清空结果' : '预览结果(只读)'}
          extra={
            <Space size="large">
              <Statistic title="解析角色" value={result.summary.targets_resolved} valueStyle={{ fontSize: 16 }} />
              {resultMode === 'clear' && (
                <>
                  <Statistic title="成功" value={result.summary.users_ok} valueStyle={{ fontSize: 16, color: '#3f8600' }} />
                  <Statistic title="失败" value={result.summary.users_failed} valueStyle={{ fontSize: 16, color: '#cf1322' }} />
                  <Statistic title="跳过" value={result.summary.users_skipped} valueStyle={{ fontSize: 16 }} />
                </>
              )}
              <Statistic title="解析失败" value={result.summary.targets_failed} valueStyle={{ fontSize: 16, color: result.summary.targets_failed ? '#cf1322' : undefined }} />
            </Space>
          }
        >
          {result.failures && result.failures.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${result.failures.length} 个输入未能解析`}
              description={
                <div style={{ maxHeight: 120, overflow: 'auto' }}>
                  {result.failures.map((f, i) => (
                    <div key={`f-${i}`}><Text code>{f.input}</Text>: {f.error}</div>
                  ))}
                </div>
              }
            />
          )}
          <Table
            rowKey={(r) => `${r.game_user_id}`}
            size="small"
            columns={resultColumns}
            dataSource={result.results}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 个角色` }}
          />
        </Card>
      )}
    </div>
  );
}

export default ItemClearManagement;
